import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Button, Container, DataGrid, Divider, Link, Spacer, Typography, useSnackbar } from '@emburse/embark-core';
import { getReviewCount, finishSetup } from '../../../../api/SSI';
import QuickSetupModal from '../../../../views/QuickSetup/components/QuickSetupModal';
import { $t, useTranslation } from '../../../../hooks/useTranslation';
import { logger } from '../../../../util/logger';
const Review = forwardRef(({ onReviewClick }, ref) => {
    useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const defaultTableData = {
        DeptCount: 0,
        UsrCount: 0,
        ExpCatCount: 0,
        EmpGLDCount: 0,
        ExpRptGLDCount: 0
    };
    const [tableData, setTableData] = useState(defaultTableData);
    const [openModal, setOpenModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const snackbarHandler = useCallback((message, severity) => {
        enqueueSnackbar(message, { variant: 'alert', AlertProps: { severity } });
    }, [enqueueSnackbar]);
    useEffect(() => {
        setLoading(true);
        getReviewCount()
            .then((data) => {
            setTableData((prevState) => data || prevState);
        })
            .catch((err) => {
            logger.log(err);
            snackbarHandler(err?.Message || err, 'error');
        })
            .finally(() => setLoading(false));
    }, [snackbarHandler]);
    useImperativeHandle(ref, () => ({
        getData() {
            return JSON.stringify(tableData);
        }
    }));
    const completeSetup = () => {
        finishSetup().catch((err) => {
            logger.log(err);
            snackbarHandler(err?.Message || err, 'error');
        });
    };
    const onFinishClick = () => {
        setOpenModal(true);
        completeSetup();
    };
    const dataMap = {
        Departments: 'DeptCount',
        Users: 'UsrCount',
        'Expense Categories': 'ExpCatCount',
        'Employee General Ledger Dimensions': 'EmpGLDCount',
        'Expense Report General Ledger Dimensions': 'ExpRptGLDCount'
    };
    const tableRows = [
        {
            id: 1,
            Step: 'Departments',
            Amount: 0,
            Link: 1
        },
        {
            id: 2,
            Step: 'Users',
            Amount: 0,
            Link: 2
        },
        {
            id: 3,
            Step: 'Expense Categories',
            Amount: 0,
            Link: 3
        },
        {
            id: 4,
            Step: 'Employee General Ledger Dimensions',
            Amount: 0,
            Link: 7
        },
        {
            id: 5,
            Step: 'Expense Report General Ledger Dimensions',
            Amount: 0,
            Link: 9
        }
    ];
    return (_jsxs(Container, { disableGutters: true, children: [_jsx(QuickSetupModal, { open: openModal, showCancel: false, onConfirm: () => {
                    window.onbeforeunload = null;
                    window.open('./Home.aspx', '_self');
                }, onClose: () => setOpenModal(false), header: $t('Congratulations on Completing Quick Setup!'), confirmButtonText: $t('Back to Homepage'), body: _jsxs(_Fragment, { children: [_jsx(Typography, { children: $t(`Your organization is ready to start using Certify!
                            Below are a few steps we recommend to finalize your implementation.`) }), _jsx(Spacer, { size: 15 }), _jsxs(Typography, { children: [$t('1. '), _jsx(Link, { href: "https://emburse.wistia.com/medias/vqkicjlm0v", target: "_blank", underline: "none", children: $t('Test your Configuration') }), $t(' to ensure a smooth rollout')] }), _jsx(Spacer, { size: 10 }), _jsxs(Typography, { children: [$t('2. '), _jsx(Link, { href: "https://help.certify.com/hc/en-us/articles/15460846952717-Sending-Welcome-Emails", target: "_blank", underline: "none", children: $t('Invite') }), $t(' your employees')] }), _jsx(Spacer, { size: 10 }), _jsxs(Typography, { children: [$t('3. '), $t('Train your employees with resources from our '), _jsx(Link, { href: "https://help.certify.com/hc/en-us/sections/16070692964749-Certify-Expense-Video-Tutorials", target: "_blank", underline: "none", children: $t('Help Center') })] }), _jsx(Spacer, { size: 10 }), _jsxs(Typography, { children: [$t('4. '), $t('Contact '), _jsx(Link, { href: "mailto:activate@certify.com", target: "_blank", underline: "none", children: "activate@certify.com" }), $t(' to enable additional features, such as Credit Card integrations, custom export files, or '), _jsx(Link, { href: "https://www.certify.com/solutions/payments", target: "_blank", underline: "none", children: $t('ACH reimbursement services') })] })] }) }), _jsx(Typography, { variant: "h4", children: $t('Review') }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t("You're almost done! The numbers below represent how many values of each type you've created.") }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t("Verify that the data you've uploaded looks correct, and click the blue button to inform a member of our team.") }), _jsx(Spacer, { size: 10 }), _jsxs(Typography, { children: [$t('To invite the users you’ve added to log in to Certify, you can send them a welcome email by following '), _jsx(Link, { href: "https://help.certify.com/hc/en-us/articles/15460846952717-Sending-Welcome-Emails", target: "_blank", children: $t('these instructions') }), "."] }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t('Congratulations on completing Quick Setup!') }), _jsx(Spacer, { size: 35 }), _jsx(Typography, { variant: "h5", children: $t('My Information') }), _jsx(Spacer, { size: 35 }), _jsx(DataGrid, { autoHeight: true, loading: loading, pageSizeOptions: [5, 10, 100], hideFooter: true, columnHeaderHeight: 0, sx: { maxWidth: '450px' }, rows: tableRows, columns: [
                    {
                        field: 'Step',
                        headerName: '',
                        flex: 8,
                        hideSortIcons: true,
                        sortable: false
                    },
                    {
                        field: 'Amount',
                        headerName: '',
                        flex: 1,
                        hideSortIcons: true,
                        sortable: false,
                        renderCell: (cell) => (_jsx(Typography, { children: tableData[dataMap[cell.row.Step]] }))
                    },
                    {
                        field: 'Link',
                        headerName: '',
                        flex: 2,
                        hideSortIcons: true,
                        sortable: false,
                        renderCell: (cell) => (_jsx(Button, { variant: "text", onClick: () => onReviewClick(cell.value), children: $t('View') }))
                    }
                ] }), _jsx(Divider, { orientation: "horizontal", style: { maxWidth: '450px' }, light: true }), _jsx(Spacer, { size: 40 }), _jsx(Button, { onClick: onFinishClick, children: $t('Finish My Setup') }), _jsx(Spacer, { size: 40 })] }));
});
export default Review;
